/**
 * WebhookInspector — Phase 6b-1.
 *
 * Trigger node that fires the workflow from an inbound HTTP request.
 * Source schema: soarNodes.js:117. Fields:
 *
 *   path           string         URL path (e.g. /webhooks/security-alert)
 *   method         enum           GET | POST | PUT | DELETE | PATCH
 *   headers        object|string  Expected headers (validation)
 *   authRequired   boolean        Require API-key auth (default true)
 *
 * Pure inbound contract — no upstream node concerns, no template strings.
 * Spread-preservation contract per useCanvas:1098.
 */

import React from 'react';

const METHODS = ['GET', 'POST', 'PUT', 'DELETE', 'PATCH'];

export default function WebhookInspector({ node, onUpdate, readOnly = false }) {
  const cfg = node.config || {};
  const path = cfg.path || '';
  const method = (cfg.method || 'POST').toUpperCase();
  const headersDisplay = stringifyJson(cfg.headers);
  // `authRequired` defaults to true per schema — treat undefined as true.
  const authRequired = cfg.authRequired === false ? false : true;

  const setField = (key, value) => {
    if (readOnly || typeof onUpdate !== 'function') return;
    onUpdate(node.id, { config: { ...cfg, [key]: value } });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <FieldGroup
        label="Webhook Path"
        hint="Leading slash. Lowercase, hyphenated. e.g. /webhooks/security-alert"
      >
        <input
          type="text"
          value={path}
          onChange={(e) => setField('path', e.target.value)}
          placeholder="/webhooks/security-alert"
          disabled={readOnly}
          autoComplete="off"
          spellCheck={false}
          style={{ ...inputStyle, fontFamily: 'SFMono-Regular, Menlo, monospace', fontSize: 11 }}
        />
      </FieldGroup>

      <FieldGroup label="HTTP Method">
        <select
          value={METHODS.includes(method) ? method : 'POST'}
          onChange={(e) => setField('method', e.target.value)}
          disabled={readOnly}
          style={selectStyle}
        >
          {METHODS.map((m) => <option key={m} value={m}>{m}</option>)}
        </select>
      </FieldGroup>

      <FieldGroup
        label="Expected Headers (JSON)"
        hint='{ "x-api-key": "required" } — validates incoming requests.'
      >
        <textarea
          value={headersDisplay}
          onChange={(e) => setField('headers', parseJsonInput(e.target.value))}
          placeholder='{ "x-api-key": "required" }'
          disabled={readOnly}
          rows={3}
          spellCheck={false}
          style={{
            ...inputStyle,
            fontFamily: 'SFMono-Regular, Menlo, monospace',
            fontSize: 11,
            lineHeight: 1.4,
            resize: 'vertical',
          }}
        />
      </FieldGroup>

      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#0f172a' }}>
        <input
          type="checkbox"
          checked={authRequired}
          onChange={(e) => setField('authRequired', e.target.checked)}
          disabled={readOnly}
        />
        Require API key authentication
      </label>
      <span style={{ fontSize: 10, color: '#64748b', marginTop: -8, marginLeft: 22 }}>
        Off only for explicitly public webhooks. Strongly recommended on for any
        production endpoint.
      </span>
    </div>
  );
}

/* ───── helpers ───────────────────────────────────────────────────── */

function stringifyJson(v) {
  if (v === undefined || v === null) return '';
  if (typeof v === 'string') return v;
  try { return JSON.stringify(v, null, 2); } catch (_) { return String(v); }
}

function parseJsonInput(raw) {
  if (typeof raw !== 'string') return raw;
  if (raw.trim() === '') return undefined;
  try { return JSON.parse(raw); } catch (_) { return raw; }
}

/* ───── shared field UI ───────────────────────────────────────────── */

function FieldGroup({ label, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <span style={{ fontSize: 11, fontWeight: 600, color: '#0f172a' }}>{label}</span>
      {children}
      {hint && (
        <span style={{ fontSize: 10, color: '#64748b' }}>{hint}</span>
      )}
    </label>
  );
}

const inputStyle = {
  width: '100%',
  padding: '6px 8px',
  fontSize: 12,
  color: '#0f172a',
  background: '#ffffff',
  border: '0.5px solid #cbd5e1',
  borderRadius: 4,
  outline: 'none',
  fontFamily: 'inherit',
};
const selectStyle = { ...inputStyle, appearance: 'auto' };
